import React, { useState } from 'react';
import { Paper, Text, Button, Stack, Group, Collapse, Divider, Badge, Modal } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import moment from 'moment';
import { useSalon } from '../../../../../context/SalonContext';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber } from '../../../../../utils/phoneUtils';
import styles from './AppointmentListItem.module.css';

function AppointmentListItem({
  appointment,
  expandedId,
  selectedEvent,
  toggleExpand,
  onClickEditAppointment,
  onStatusChange
}) {
  const navigate = useNavigate();
  const { businessInfo } = useSalon();
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const handleAccept = () => {
    if (businessInfo.appts_need_confirmation) {
      onStatusChange(appointment.id, 'Accepted');
    } else {
      onStatusChange(appointment.id, 'Confirmed');
    }
  };

  const handleDecline = () => {
    setIsDeclineModalOpen(false);
    onStatusChange(appointment.id, 'Declined');
  };

  const handleConfirm = () => {
    onStatusChange(appointment.id, 'Confirmed');
  };

  const handleComplete = () => {
    if (businessInfo.stripe_terminal_device_id) {
      navigate(`/checkout?appt_id=${appointment.id}`);
    } else {
      onStatusChange(appointment.id, 'Completed');
    }
  };

  const handleNoShow = () => {
    onStatusChange(appointment.id, 'No Show');
  };

  const renderAppointmentButtons = () => {
    switch (appointment.status) {
      case 'Requested':
        return (
          <Stack spacing="sm" mt="md" w="100%">
            <Button 
              fullWidth
              size="sm"
              className={styles.primaryAction}
              onClick={handleAccept}
            >
              Accept
            </Button>
            <Button
              fullWidth
              variant="subtle"
              size="sm"
              className={styles.secondaryAction}
              onClick={() => onClickEditAppointment(appointment)}
            >
              Reschedule or Cancel
            </Button>
            <Button
              fullWidth
              variant="light"
              size="sm"
              className={styles.destructiveAction}
              onClick={() => setIsDeclineModalOpen(true)}
            >
              Decline
            </Button>
          </Stack>
        );
      case 'Accepted':
        return (
          <Stack spacing="sm" mt="md" w="100%">
            <Button
              fullWidth
              size="sm"
              className={styles.primaryAction}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
            <Button
              fullWidth
              variant="subtle"
              size="sm"
              className={styles.secondaryAction}
              onClick={() => onClickEditAppointment(appointment)}
            >
              Reschedule or Cancel
            </Button>
            <Button
              fullWidth
              variant="light"
              size="sm"
              className={styles.destructiveAction}
              onClick={handleNoShow}
            >
              Mark No Show
            </Button>
          </Stack>
        );
      case 'Confirmed':
        return (
          <Stack gap="xs" mt="md" w="100%">
            <Button
              fullWidth
              size="sm"
              className={styles.primaryAction}
              onClick={handleComplete}
            >
              {businessInfo.stripe_terminal_device_id ? 'Checkout' : 'Mark as Completed'}
            </Button>
            <Button
              fullWidth
              variant="subtle"
              size="sm"
              className={styles.secondaryAction}
              onClick={() => onClickEditAppointment(appointment)}
            >
              Reschedule or Cancel
            </Button>
            <Button
              fullWidth
              variant="light"
              size="sm"
              className={styles.destructiveAction}
              onClick={handleNoShow}
            >
              Mark No Show
            </Button>
          </Stack>
        );
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return '#8b5cf6';
      case 'Requested':
        return 'yellow';
      case 'Accepted':
        return 'orange';
      case 'Confirmed':
        return 'green';
      case 'No Show':
        return 'red';
      default:
        return 'gray';
    }
  };

  const StatusPill = ({ status }) => (
    <Badge 
      color={getStatusColor(status)}
      variant="light"
      size="sm"
      style={{ position: 'absolute', top: '12px', left: '12px' }}
    >
      {status}
    </Badge>
  );

  return (
    <>
      <Paper 
        id={`appointment-${appointment.id}`}
        shadow="xs" 
        p="md"
        className={`appointment-item ${selectedEvent?.id === appointment.id ? 'selected' : ''}`}
        style={{ position: 'relative' }}
      >
        <StatusPill status={appointment.status} />
        <div onClick={() => toggleExpand(appointment.id)} style={{ cursor: 'pointer', marginTop: '24px' }}>
          <Text size="xs" mt="xs">
            {moment(appointment.start).format('ddd MM/DD/YY')} • {moment(appointment.start).format('h:mm A')} - {moment(appointment.end).format('h:mm A')}
          </Text>
          <Text style={{fontWeight: 500}} size="lg">{appointment.title}</Text>
          <Text size="sm" c="dimmed">Serviced by {appointment.employeeName}</Text>
        </div>

        <Collapse in={expandedId === appointment.id}>
          <Divider my="md" />
          
          <Stack gap="sm">
            <Group position="apart" align="center">
              <Text size="md">Appointment Details</Text>
              <Button 
                variant="transparent" 
                color="blue" 
                size="sm"
                ml="auto"
                leftSection={<IconEdit size={16} />}
                onClick={() => onClickEditAppointment(appointment)}
              >
                Edit
              </Button>
            </Group>

            <Stack gap="sm">
              <div>
                <Text style={{fontWeight: 600}} size="sm">Client</Text>
                <Stack gap={2}>
                  <Text size="sm">{appointment.firstName} {appointment.lastName}</Text>
                  <Text size="sm">{formatPhoneNumber(appointment.phone)}</Text>
                  <Text size="sm">{appointment.email}</Text>
                </Stack>
              </div>

              <div>
                <Text style={{fontWeight: 600}} size="sm">Artist</Text>
                <Text size="sm">{appointment.employeeName}</Text>
              </div>

              <div>
                <Text style={{fontWeight: 600}} size="sm">Schedule</Text>
                <Stack gap="0">
                  <Text size="sm">{moment(appointment.start).format('dddd M/D/YY')}</Text>
                  <Text size="sm">
                    {moment(appointment.start).format('h:mm A')} - {moment(appointment.end).format('h:mm A')}
                  </Text>
                </Stack>
              </div>

              <div>
                <Text style={{fontWeight: 600}} size="sm">Service</Text>
                <Text size="sm">{appointment.title}</Text>
              </div>

              <div>
                <Text style={{fontWeight: 600}} size="sm">Price</Text>
                <Text size="sm">${appointment.price}</Text>
              </div>

              <div>
                <Text style={{fontWeight: 600}} size="sm">Customer Notes</Text>
                <Text size="sm">{appointment.customerNotes || '-'}</Text>
              </div>

              <div>
                <Text style={{fontWeight: 600}} size="sm">Salon Notes</Text>
                <Text size="sm">{appointment.salonNotes || '-'}</Text>
              </div>
            </Stack>

            {renderAppointmentButtons()}
          </Stack>
        </Collapse>
      </Paper>

      <Modal
        opened={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        title="Confirm Decline"
        size="sm"
        centered
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to decline this appointment?
          </Text>
          <Text size="sm" c="dimmed">
            This will notify the customer that their appointment request has been declined.
          </Text>
          
          <Group mt="md" justify="space-between">
            <Button
              variant="subtle"
              className={styles.secondaryAction}
              onClick={() => setIsDeclineModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="light"
              className={styles.destructiveAction}
              onClick={handleDecline}
            >
              Decline Appointment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default AppointmentListItem; 